import {
  ROUTE_DISCOVER_INVESTING_IDEAS,
  ROUTE_DISCOVER_SAMPLE_PORTFOLIOS,
  ROUTE_CREATE_CRITERIA,
} from '@/constants/routes';
import { OneColumn } from '@components/Layout';
import { Column, TabList } from '@simplywallst/ui-core';
import ScreenerOutline from '@simplywallst/ui-core/icons/ScreenerOutline';
import MobileTopbarSubNav, { ListItem } from '@components/MobileTopbarSubNav';
import { discoverRoutes } from '@/pages/DiscoverPageV2/constants';
import { RouterLink } from '@redux-first-router-link';
import { DesktopSubNav, CTA, TabLink, PageTitle } from './styled';
import ExploreIdeas from '../ExploreIdeas';
import ExplorePortfolios from '../ExplorePortfolios';
import Media from 'react-media';
import { useTheme } from '@simplywallst/ui-core';
import { useUserAgent } from '@simplywallst/hooks/useUserAgent';
import { useTracking } from 'react-tracking';
import If from '@/components/If';
import loadable from '@loadable/component';
import { useFlag } from '@/features/unleash';

const ScreenerCarousel = loadable(() => import('../ScreenerCarousel'));
const DiscoverHubScreensSlider = loadable(
  () => import('../ScreenerCarousel/DiscoverHubScreensSlider')
);
export interface Props {
  route: string;
}

const ScreenerCallToAction = () => (
  <CTA>
    Have your own idea? Try our{' '}
    <RouterLink inheritColor to={{ type: ROUTE_CREATE_CRITERIA }}>
      Stock Screener
    </RouterLink>
    <ScreenerOutline />
  </CTA>
);

const Discover = ({ route }: Props) => {
  const { isDesktop, isMobile, isTablet } = useUserAgent();
  const theme = useTheme();
  const { trackEvent } = useTracking();

  const featureFlag = useFlag({ name: 'mono-discover-hub-screener-carousel' });
  const isShownCarousel =
    route === ROUTE_DISCOVER_INVESTING_IDEAS &&
    featureFlag.flagsReady &&
    featureFlag.enabled &&
    featureFlag.variant.name === 'variant';

  return (
    <OneColumn>
      <Media query={{ minWidth: theme.lgMinRaw }} defaultMatches={isDesktop}>
        <DesktopSubNav alignItem="center" justifyContent="center">
          <TabList templateColumns="repeat(3, max-content)">
            {discoverRoutes.map(({ to, label }, i) => (
              <TabLink
                key={`tab-${to}-${i}`}
                inheritColor
                noUnderline
                data-cy-id={`nav-${i}`}
                selected={route === to}
                styleType="subnav"
                to={{ type: to || '' }}
                onClick={() =>
                  trackEvent({
                    type: 'track',
                    subject: 'subNav',
                    modifier: typeof label === 'string' ? label : undefined,
                    action: 'click',
                  })
                }
              >
                {label}
              </TabLink>
            ))}
          </TabList>
          <Column alignItem="flex-end" justifyContent="center" fillVoid>
            <ScreenerCallToAction />
          </Column>
        </DesktopSubNav>
      </Media>
      <Media
        query={{ maxWidth: theme.mdMaxRaw }}
        defaultMatches={isMobile || isTablet}
      >
        <MobileTopbarSubNav routes={discoverRoutes} selected={route}>
          {({ routes, handleClick, refMap, selected }) =>
            routes.map(({ to, label, tracking }, i) => (
              <ListItem
                ref={refMap[i]}
                key={`MobileTopbarListItem-${to}-${i}`}
                selected={selected === to}
              >
                <RouterLink
                  inheritColor
                  noUnderline
                  to={{ type: to || '' }}
                  onClick={() => handleClick(tracking || label, to)}
                >
                  {label}
                </RouterLink>
              </ListItem>
            ))
          }
        </MobileTopbarSubNav>
      </Media>
      <PageTitle data-cy-id="discover-header-title">
        <If
          conditions={{
            investingIdeas: route === ROUTE_DISCOVER_INVESTING_IDEAS,
            samplePortfolios: route === ROUTE_DISCOVER_SAMPLE_PORTFOLIOS,
          }}
        >
          {{
            investingIdeas: 'Investing Ideas',
            samplePortfolios: 'Sample Portfolios',
          }}
        </If>
      </PageTitle>
      <Media
        query={{ maxWidth: theme.mdMaxRaw }}
        defaultMatches={isMobile || isTablet}
      >
        <ScreenerCallToAction />
      </Media>

      {isShownCarousel && (
        <ScreenerCarousel content={<DiscoverHubScreensSlider />} />
      )}

      {route === ROUTE_DISCOVER_INVESTING_IDEAS && <ExploreIdeas />}
      {route === ROUTE_DISCOVER_SAMPLE_PORTFOLIOS && <ExplorePortfolios />}
    </OneColumn>
  );
};

export default Discover;
